// Pantalla Compromisos (`/collections/promises`): el explorer de 3 columnas del diseño —filtros ·
// tabla · detalle—. Las filas salen de un fixture local; no hay endpoint que las devuelva todavía,
// así que toda la pantalla va bajo `<MockBoundary>`.
import { useCallback, useMemo, useState } from 'react';
import { mxDay } from '@/lib/dateHelpers';
import { MockBoundary } from '@/shared/components/MockBoundary';
import { MasterDetailExplorer } from '@/shared/patterns/MasterDetail';
import { screenFrameClass } from '@/shared/patterns/ScreenFrame';
import { StatsBar } from '@/shared/patterns/StatsBar';
import { buildPromisesFixture } from './fixtures';
import { formatPromiseMoney, promisesStatsItems } from './format';
import {
    activeFilterCount,
    applyStatFilter,
    computePromiseStats,
    filterPromises,
    sortPromises,
    summarizePromises,
} from './logic';
import { emptyPromisesFilters, type PromiseStatKey, type PromisesFilters } from './types';
import { PromiseDetail } from './components/PromiseDetail';
import { PromisesFiltersPanel } from './components/PromisesFiltersPanel';
import { PromisesTable } from './components/PromisesTable';

/** Barra de herramientas de la tabla (`.table-toolbar` del diseño). */
const TOOLBAR =
    'flex flex-none flex-wrap items-center gap-2 border-b border-border px-3 py-1.5 text-[11px] text-muted-foreground';

export interface PromisesPageProps {
    /** El día contra el que miden Hoy y Mañana. Los tests lo fijan; en el navegador es el de CDMX. */
    today?: string;
}

export function PromisesPage({ today }: PromisesPageProps) {
    const day = useMemo(() => today ?? mxDay(new Date().toISOString()), [today]);
    const allRows = useMemo(() => buildPromisesFixture(day), [day]);

    const [filters, setFilters] = useState<PromisesFilters>(emptyPromisesFilters);
    const [filtersCollapsed, setFiltersCollapsed] = useState(false);
    const [stat, setStat] = useState<PromiseStatKey>('all');
    const [pickedPromiseId, setPickedPromiseId] = useState<string | null>(null);

    const visibleRows = useMemo(
        () => sortPromises(filterPromises(applyStatFilter(allRows, stat, day), filters)),
        [allRows, stat, day, filters],
    );

    const stats = useMemo(() => computePromiseStats(allRows, day), [allRows, day]);
    const summary = useMemo(() => summarizePromises(visibleRows), [visibleRows]);

    // Sin elección propia se abre el primero de la lista, como el mock; si los filtros lo sacaron,
    // el detalle vuelve a su estado vacío en vez de mostrar una fila que ya no está.
    const selected =
        visibleRows.find((row) => row.promiseId === pickedPromiseId) ?? visibleRows.at(0) ?? null;

    const handleFiltersChange = useCallback((next: PromisesFilters) => {
        setFilters(next);
    }, []);

    return (
        <MockBoundary active label="Mock · sin backend" className={screenFrameClass('fill')}>
            <MasterDetailExplorer
                filtersCollapsed={filtersCollapsed}
                storageKey="collections-promises"
                filters={
                    <PromisesFiltersPanel
                        filters={filters}
                        onChange={handleFiltersChange}
                        today={day}
                        collapsed={filtersCollapsed}
                        onToggleCollapsed={() => {
                            setFiltersCollapsed((prev) => !prev);
                        }}
                    />
                }
                list={
                    <>
                        <StatsBar
                            items={promisesStatsItems(stats)}
                            active={stat}
                            onChange={setStat}
                        />
                        <div className={TOOLBAR}>
                            <span>{`${String(summary.count)} compromisos`}</span>
                            {activeFilterCount(filters) === 0 ? null : (
                                <span className="rounded bg-primary/10 px-1.5 py-[1px] text-[10px] font-bold text-primary">
                                    {`${String(activeFilterCount(filters))} filtros`}
                                </span>
                            )}
                            <span className="flex-1" />
                            <span>
                                {formatPromiseMoney(summary.totalAmountCents)}
                                {' · Cure rate '}
                                <span className="font-semibold text-info">{`${String(summary.cureRatePct)}%`}</span>
                            </span>
                        </div>
                        <PromisesTable
                            rows={visibleRows}
                            selectedPromiseId={selected?.promiseId ?? null}
                            onSelect={setPickedPromiseId}
                        />
                    </>
                }
                detail={<PromiseDetail promise={selected} />}
            />
        </MockBoundary>
    );
}
